import React, { useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { dayOptions, getYears, monthOptions } from "shared/lib/functions";
import { formattedMessageParams } from "shared/messages";
import { BookingFormDateProps } from "shared/types";

import FormErrorText from "@components/UI/Form/FormErrorText";
import SimpleSelect, { FormControl } from "@components/UI/SimpleSelect";

const { dayText, monthText, yearText } = defineMessages({
  dayText: formattedMessageParams("Date.day"),
  monthText: formattedMessageParams("Date.month"),
  yearText: formattedMessageParams("Date.year"),
});

const customStyle = {
  container: (base) => ({
    ...base,
    flexGrow: 1,
    maxWidth: "400px",
  }),
  valueContainer: (base) => ({
    ...base,
  }),

  placeholder: (base) => ({
    ...base,
  }),
};

export default function BookingFormDate({
  value,
  onChange,
  isError,
  errorType,
  name,
  dateRange,
}: BookingFormDateProps) {
  const intl = useIntl();
  const [defaultDateRange, setDefaultDateRange] = useState(null);

  const BIRTH_MAX_AGO_YEARS = 150;
  const PASSPORT_MAX_EXPIRATION_YEARS = 50;
  const PASSENGER_BIRTH_DATE_TESTID = "passenger_birth_date";
  const PASSPORT_EXPIRY_DATE_TESTID = "PassportExpiryDate";

  useEffect(() => {
    if (!dateRange) {
      const today = new Date();

      if (name.includes(PASSENGER_BIRTH_DATE_TESTID)) {
        setDefaultDateRange({
          from: new Date(
            today.getFullYear() - BIRTH_MAX_AGO_YEARS,
            today.getMonth(),
            today.getDate()
          )
            .toISOString()
            .split("T")[0],
          to: today.toISOString().split("T")[0],
        });
      }

      if (name.includes(PASSPORT_EXPIRY_DATE_TESTID)) {
        setDefaultDateRange({
          from: today.toISOString().split("T")[0],
          to: new Date(
            today.getFullYear() + PASSPORT_MAX_EXPIRATION_YEARS,
            today.getMonth(),
            today.getDate()
          )
            .toISOString()
            .split("T")[0],
        });
      }
    }
  }, [dateRange, name]);

  const props = { name };
  const showError = false;

  const { year, month, day, lang } = useBookingFormDate({ value });

  return (
    <>
      <div className="date-input-date-container">
        <div className="date-input-field date-input-field-day">
          <SimpleSelect
            testId={`${props.name}-day`}
            components={{ Control: FormControl }}
            controlOptions={{ value: day, error: showError }}
            styles={customStyle}
            options={dayOptions()}
            placeholder={intl.formatMessage(dayText)}
            value={day && { value: day, label: day }}
            onChange={(newValue) =>
              onChange(getFullDateValue(2, newValue, value))
            }
          />
        </div>
        <div className="date-input-field date-input-field-month">
          <SimpleSelect
            testId={`${props.name}-month`}
            components={{ Control: FormControl }}
            controlOptions={{ value: month, error: showError }}
            styles={customStyle}
            options={monthOptions(lang)}
            value={
              month && monthOptions(lang).find((opt) => opt.value === month)
            }
            placeholder={intl.formatMessage(monthText)}
            onChange={(newValue) =>
              onChange(getFullDateValue(1, newValue, value))
            }
          />
        </div>
        <div className="date-input-field-year">
          <SimpleSelect
            testId={`${props.name}-year`}
            components={{ Control: FormControl }}
            controlOptions={{ value: year, error: showError }}
            styles={customStyle}
            options={getYears({
              dateRange: dateRange || defaultDateRange,
              isFuture:
                props.name.includes("validity") ||
                props.name.includes("Expiry"),
            })}
            value={year && { value: year, label: year }}
            placeholder={intl.formatMessage(yearText)}
            onChange={(newValue) =>
              onChange(getFullDateValue(0, newValue, value))
            }
          />
        </div>
      </div>

      {isError && (
        <div className="form-text-input-error">
          <FormErrorText error={errorType} />
        </div>
      )}
    </>
  );
}

function useBookingFormDate({ value }) {
  const lang = useSelector((state) => state.requestorConfig.currentLanguage);

  if (!value || value === "0000-00-00") {
    return { year: null, month: null, day: null, lang };
  }

  const [year, month, day] = value.split("-");

  return {
    year: Number(year) > 0 && year,
    month: Number(month) > 0 && month,
    day: Number(day) > 0 && day,
    lang,
  };
}

function getFullDateValue(indexField, newValue, oldValue) {
  const dateValues = oldValue ? oldValue.split("-") : ["0000", "00", "00"];
  dateValues[indexField] = newValue.value;
  const result = dateValues.join("-");

  return result;
}
